import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  formatBrandMoney,
  getBrandDefaultCurrency,
  getBrandCurrencySuffix,
  getBrandCurrencyOptions,
  getBrandLocale,
  normalizeBrandCurrency,
  isBrandCurrencyLocked,
  getBrandVatRate,
  getBrandPriceTierThresholds,
  SAUDI_CURRENCY_CODE,
} from '../../shared/utils/brandCurrency';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Tags, Plus, Search, X, Filter, Trash2,
  ChevronDown, ChevronUp,
  CheckCircle, Calendar, DollarSign, Package, Layers, LayoutGrid,
  Hash, FileJson,
} from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, exactColumnRule, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import localApiAdapter from '../../core/services/localApiAdapter';
import { CustomSelect, CustomNumberInput, CustomDatePicker } from '../../shared/components/custom';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { shouldShowCardsToolbarSearch } from '../../shared/utils/listPageSearchVisibility';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import { useTranslation } from 'react-i18next';
import FacturationListPageLayout, {
  facturationTableScrollClass,
} from '../facturation/components/FacturationListPageLayout';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import {
  countArticleActive,
  countArticleInactive,
  isArticleActiveStatus,
  matchesArticleStatusFilter,
  getArticleStatusLabel,
} from './articlesStatusUtils';
import {
  buildArticleStatusBadgeOptions,
  buildArticleStatusColumnFilterOptions,
  buildArticleStatusSelectOptions,
  getCatalogStatusBadgeStyle,
} from './articleCatalogStatusOptions';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import {buildBulkDeleteFilterAction,
  buildArticlesImportExportProps,
  createArticleStatusChangeHandler,
  QAWAEM_TABLE_ACTIONS_TH_CLASS,
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useFilteredListRowSelection,
  useQawaemColumnVisibility, QawaemTableStickyActionsTd, ArticlesConfigNotice, sliceArticlesListItems } from './articlesListPageUtils';
import { useArticlesConfig, useArticlesListViewMode } from './useArticlesConfig';
import { fetchCatalogProducts, useCatalogProductsRefreshListener } from './catalogProductOptions';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import { AppConfirmDialog } from '../../shared/components/AppConfirmDialog';
import { useCrudDeleteConfirm } from '../../shared/hooks/useCrudDeleteConfirm';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { generateEntityReference, getEntityPrefix } from '../../shared/utils/generateEntityReference';
import { useSettings } from '../../core/contexts/SettingsContext';

const NS = 'modules.article.pages.priceListsPage';
const API_BASE = '/api/articles/price-lists';

const EMPTY_LINE = {
  product_id: '',
  product_name: '',
  variant_id: '',
  variant_name: '',
  unit_price: '',
  min_qty: '',
  discount_pct: '',
  vat_rate: '',
};

function buildClientSegmentOptions(p) {
  return [
    { value: 'particulier', label: p('segment_particulier'), isDefault: true },
    { value: 'professionnel', label: p('segment_professionnel'), isDefault: true },
    { value: 'gros', label: p('segment_gros'), isDefault: true },
    { value: 'VIP', label: p('segment_vip'), isDefault: true },
  ];
}

function resolveClientSegmentLabel(segment, options) {
  if (!segment) return '';
  const match = options.find((opt) => opt.value === segment);
  return match?.label || segment;
}

function flattenPriceListsForExport(lists) {
  const rows = [];
  for (const list of lists) {
    const header = {
      list_number: list.list_number || '',
      name: list.name || '',
      currency: list.currency || '',
      valid_from: list.valid_from ? String(list.valid_from).split('T')[0] : '',
      valid_to: list.valid_to ? String(list.valid_to).split('T')[0] : '',
      client_segment: list.client_segment || '',
      status: list.status || '',
      price_includes_vat: list.price_includes_vat || 'exclusive',
      notes: list.notes || '',
    };
    const lineItems = Array.isArray(list.lines) && list.lines.length > 0 ? list.lines : [null];
    for (const line of lineItems) {
      rows.push({
        ...header,
        product_id: line?.product_id ?? '',
        product_name: line?.product_name ?? '',
        variant_id: line?.variant_id ?? '',
        variant_name: line?.variant_name ?? '',
        unit_price: line?.unit_price ?? '',
        min_qty: line?.min_qty ?? '',
        discount_pct: line?.discount_pct ?? '',
        vat_rate: line?.vat_rate ?? '',
      });
    }
  }
  return rows;
}

function groupFlatImportRows(rows, vatRate) {
  const groups = new Map();
  for (const row of rows) {
    const listKey = String(row.list_number || row.name || '').trim();
    if (!listKey) continue;
    if (!groups.has(listKey)) {
      groups.set(listKey, {
        list_number: row.list_number || '',
        name: String(row.name || listKey).trim(),
        currency: row.currency || null,
        valid_from: row.valid_from || null,
        valid_to: row.valid_to || null,
        client_segment: row.client_segment || null,
        status: String(row.status || 'actif').trim(),
        notes: row.notes || null,
        price_includes_vat: row.price_includes_vat || 'exclusive',
        lines: [],
      });
    }
    const group = groups.get(listKey);
    if (row.product_id || row.product_name) {
      group.lines.push({
        product_id: row.product_id || null,
        product_name: row.product_name || null,
        variant_id: row.variant_id || null,
        variant_name: row.variant_name || null,
        unit_price: parseFloat(row.unit_price) || 0,
        min_qty: parseFloat(row.min_qty) || 0,
        discount_pct: parseFloat(row.discount_pct) || 0,
        vat_rate: parseFloat(row.vat_rate) || vatRate,
      });
    }
  }
  return [...groups.values()];
}

function normalizeImportedListPayload(item, vatRate) {
  const name = String(item.name || '').trim();
  const status = String(item.status || 'actif').trim();
  if (!name || !status) return null;
  const rawLines = Array.isArray(item.lines) ? item.lines : [];
  return {
    list_number: item.list_number || undefined,
    name,
    currency: normalizeBrandCurrency(item.currency) || null,
    valid_from: item.valid_from || null,
    valid_to: item.valid_to || null,
    client_segment: item.client_segment || null,
    status,
    notes: item.notes || null,
    price_includes_vat: item.price_includes_vat || 'exclusive',
    lines: rawLines
      .filter((line) => line?.product_id || line?.product_name)
      .map((line) => ({
        product_id: line.product_id || null,
        product_name: line.product_name || null,
        variant_id: line.variant_id || null,
        variant_name: line.variant_name || null,
        unit_price: parseFloat(line.unit_price) || 0,
        min_qty: parseFloat(line.min_qty) || 0,
        discount_pct: parseFloat(line.discount_pct) || 0,
        vat_rate: parseFloat(line.vat_rate) || vatRate,
      })),
  };
}

const PriceListsPage = () => {
  const { t, i18n } = useTranslation();
  const { isDashboardLightMode } = useGlobalTheme();
  const { settings } = useSettings();
  const p = (key, opts) => t(`${NS}.${key}`, opts);
  const dateLocale = getBrandLocale(i18n.language);
  const qawaem = isSaudiBrand();
  const articlesConfig = useArticlesConfig();
  const currencyLocked = isBrandCurrencyLocked();
  const currencySuffix = getBrandCurrencySuffix();
  const vatRate = getBrandVatRate();
  const priceTiers = getBrandPriceTierThresholds();
  const fmtCurrency = (code) => normalizeBrandCurrency(code) || p('empty_dash');

  const formatPrice = (price) => {
    if (price == null || price === '') return '—';
    const n = parseFloat(price);
    if (Number.isNaN(n)) return '—';
    return formatBrandMoney(n);
  };

  const STATUS_OPTIONS = useMemo(() => buildArticleStatusBadgeOptions(t), [t]);

  const statusColumnFilterOptions = useMemo(() => buildArticleStatusColumnFilterOptions(t), [t]);

  const formStatusOptions = useMemo(() => buildArticleStatusSelectOptions(t), [t]);

  const CURRENCY_OPTIONS = useMemo(() => getBrandCurrencyOptions(), []);

  const clientSegmentOptions = useMemo(() => buildClientSegmentOptions(p), [t]);

  const clientSegmentFilterOptions = useMemo(
    () => clientSegmentOptions.map(({ value, label }) => ({ value, label })),
    [clientSegmentOptions],
  );

  const formatClientSegment = useCallback(
    (segment) => {
      const label = resolveClientSegmentLabel(segment, clientSegmentOptions);
      return label || '—';
    },
    [clientSegmentOptions],
  );

  const [priceLists, setPriceLists] = useState([]);
  const [products, setProducts] = useState([]);
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [viewMode, setViewMode] = useArticlesListViewMode(articlesConfig);
  const [lines, setLines] = useState([]);

  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_lists');
  const [quickFilter, setQuickFilter] = useState('all');
  const { dateRange, setDateRange } = useSaudiListDateRange();

  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const {
    deleteConfirm,
    closeDeleteConfirm,
    openDeleteOne,
    openBulkDelete,
    takePendingIds,
  } = useCrudDeleteConfirm();
  const entityLabel = p('entity_label', { defaultValue: 'قائمة الأسعار' });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: 'قائمة أسعار' });

  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  const [formData, setFormData] = useState({
    list_number: '',
    name: '',
    currency: getBrandDefaultCurrency(),
    valid_from: '',
    valid_to: '',
    client_segment: '',
    status: 'actif',
    notes: '',
    price_includes_vat: 'exclusive',
  });

  const productOptions = useMemo(
    () =>
      products.map((prod) => ({
        value: prod.id,
        label: prod.reference ? `${prod.name} (${prod.reference})` : prod.name || prod.id,
        isDefault: true,
      })),
    [products]
  );

  const priceIncludesVatOptions = useMemo(
    () => [
      { value: 'exclusive', label: p('price_vat_exclusive'), isDefault: true },
      { value: 'inclusive', label: p('price_vat_inclusive'), isDefault: true },
    ],
    [t]
  );

  const pricingTierHint = useMemo(
    () =>
      p('hint_pricing_tiers', {
        lowMax: formatBrandMoney(priceTiers.lowMax, false),
        highMin: formatBrandMoney(priceTiers.highMin, false),
        suffix: currencySuffix,
      }),
    [p, priceTiers, currencySuffix]
  );

  const getVariantOptions = (productId) => {
    if (!productId) {
      return [{ value: '', label: p('no_variant'), isDefault: true }];
    }
    const matches = variants.filter((v) => String(v.parent_product_id) === String(productId));
    return [
      { value: '', label: p('no_variant'), isDefault: true },
      ...matches.map((variant) => ({
        value: variant.id,
        label: variant.sku ? `${variant.name} (${variant.sku})` : variant.name || variant.id,
        isDefault: true,
      })),
    ];
  };

  const getPriceTierLabel = (price) => {
    const n = parseFloat(price);
    if (Number.isNaN(n) || n <= 0) return null;
    if (n <= priceTiers.lowMax) return p('tier_low');
    if (n >= priceTiers.highMin) return p('tier_high');
    return p('tier_mid');
  };

  useEffect(() => {
    loadPriceLists();
    loadProducts();
    loadVariants();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadPriceLists = async () => {
    try {
      setLoading(true);
      const response = await localApiAdapter.get(API_BASE);
      const data = response?.data ?? response;
      setPriceLists(sortRecordsNewestFirst(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error('Error loading price lists:', error);
      setPriceLists([]);
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      setProducts(await fetchCatalogProducts());
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
    }
  };

  useCatalogProductsRefreshListener(loadProducts);

  const loadVariants = async () => {
    try {
      const response = await localApiAdapter.get('/api/articles/variantes');
      const data = response?.data ?? response;
      setVariants(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading variants:', error);
      setVariants([]);
    }
  };

  const generateListNumber = useCallback(() => {
    const prefix = getEntityPrefix(settings, 'priceList', 'PL');
    return generateEntityReference(prefix, priceLists, (r) => r.list_number || r.id);
  }, [priceLists, settings]);

  const addLine = () => {
    setLines((prev) => [...prev, { ...EMPTY_LINE, vat_rate: String(vatRate) }]);
  };

  const removeLine = (index) => {
    setLines((prev) => prev.filter((_, i) => i !== index));
  };

  const updateLine = (index, patch) => {
    setLines((prev) => prev.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  };

  const handleLineProductSelect = (index, productId) => {
    const product = products.find((prod) => String(prod.id) === String(productId));
    updateLine(index, {
      product_id: productId || '',
      product_name: product?.name || '',
      variant_id: '',
      variant_name: '',
    });
  };

  const handleLineVariantSelect = (index, variantId) => {
    const variant = variants.find((item) => String(item.id) === String(variantId));
    updateLine(index, {
      variant_id: variantId || '',
      variant_name: variant?.name || '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.status.trim()) {
      window.alert(p('alert_required'));
      return;
    }
    try {
      setLoading(true);
      const listNumber = (formData.list_number || '').trim()
        || (articlesConfig.autoGeneratePriceListReference !== false ? generateListNumber() : '');
      const payload = {
        list_number: listNumber,
        name: formData.name.trim(),
        currency: normalizeBrandCurrency(formData.currency) || null,
        valid_from: formData.valid_from || null,
        valid_to: formData.valid_to || null,
        client_segment: formData.client_segment || null,
        status: formData.status.trim(),
        notes: formData.notes || null,
        price_includes_vat: formData.price_includes_vat || 'exclusive',
        lines: lines
          .filter((line) => line.product_id || line.product_name)
          .map((line) => ({
            product_id: line.product_id || null,
            product_name: line.product_name || null,
            variant_id: line.variant_id || null,
            variant_name: line.variant_name || null,
            unit_price: parseFloat(line.unit_price) || 0,
            min_qty: parseFloat(line.min_qty) || 0,
            discount_pct: parseFloat(line.discount_pct) || 0,
            vat_rate: parseFloat(line.vat_rate) || vatRate,
          })),
      };
      if (selectedRecord) {
        await localApiAdapter.put(`${API_BASE}/${selectedRecord.id}`, payload);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post(API_BASE, payload);
        notifyCreated(showNotify, entityLabel);
      }
      await loadPriceLists();
      resetForm();
    } catch (error) {
      console.error('Error saving price list:', error);
      notifyError(showNotify, error?.response?.data?.error || p('alert_save_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setSelectedRecord(record);
    setFormData({
      list_number: record.list_number ?? '',
      name: record.name ?? '',
      currency: normalizeBrandCurrency(record.currency ?? ''),
      valid_from: record.valid_from ? String(record.valid_from).split('T')[0] : '',
      valid_to: record.valid_to ? String(record.valid_to).split('T')[0] : '',
      client_segment: record.client_segment ?? '',
      status: record.status ?? 'actif',
      notes: record.notes ?? '',
      price_includes_vat: record.price_includes_vat ?? 'exclusive',
    });
    setLines(
      Array.isArray(record.lines) && record.lines.length > 0
        ? record.lines.map((line) => ({
            product_id: line.product_id ?? '',
            product_name: line.product_name ?? '',
            variant_id: line.variant_id ?? '',
            variant_name: line.variant_name ?? '',
            unit_price: line.unit_price != null ? String(line.unit_price) : '',
            min_qty: line.min_qty != null ? String(line.min_qty) : '',
            discount_pct: line.discount_pct != null ? String(line.discount_pct) : '',
            vat_rate: line.vat_rate != null ? String(line.vat_rate) : String(vatRate),
          }))
        : []
    );
    setShowModal(true);
  };

  

  const handleDelete = (id) => openDeleteOne(id);

  const handleBulkDelete = () => {
    if (!selectedRecords.length) return;
    openBulkDelete(selectedRecords);
  };

  const executeDeleteConfirmed = async () => {
    const ids = takePendingIds();
    if (!ids.length) return;
    try {
      if (ids.length === 1) {
        setLoading(true);
        await localApiAdapter.delete(`${API_BASE}/${ids[0]}`);
        notifyDeleted(showNotify, entityLabel);
        await loadPriceLists();
      } else {
        const deleteCount = ids.length;
        await Promise.all(ids.map((id) => localApiAdapter.delete(`${API_BASE}/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
        clearSelection();
        loadPriceLists();
      }
    } catch (error) {
      console.error('Error deleting price list:', error);
        notifyError(showNotify, error?.response?.data?.error || p('alert_save_error'));
      } finally {
        setLoading(false);
    }
  };

  const handleView = (record) => {
    setSelectedRecord(record);
    setShowViewModal(true);
  };

  const resetForm = () => {
    setFormData({
      list_number: '',
      name: '',
      currency: getBrandDefaultCurrency(),
      valid_from: '',
      valid_to: '',
      client_segment: '',
      status: 'actif',
      notes: '',
      price_includes_vat: 'exclusive',
    });
    setLines([]);
    setSelectedRecord(null);
    setShowModal(false);
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString(dateLocale);
  };

  const handleStatusChange = useCallback(
    createArticleStatusChangeHandler(API_BASE, loadPriceLists),
    [],
  );

  const getStatusBadge = (status) => (
    <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${getCatalogStatusBadgeStyle(status)}`}>
      {getArticleStatusLabel(status, t)}
    </span>
  );

  const stats = {
    total: priceLists.length,
    actifs: countArticleActive(priceLists),
    inactifs: countArticleInactive(priceLists),
    totalLines: priceLists.reduce((sum, r) => sum + (Array.isArray(r.lines) ? r.lines.length : 0), 0),
    segments: new Set(priceLists.map((r) => r.client_segment).filter(Boolean)).size,
  };

  const priceListsDistribution = {
    all_lists: {
      title: p('dist_overview'),
      data: [
        { name: p('stat_active'), count: stats.actifs, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { name: p('stat_inactive'), count: stats.inactifs, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { name: p('stat_lines'), count: stats.totalLines, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { name: p('stat_segments'), count: stats.segments, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
      ],
    },
    by_status: {
      title: t('common.by_status'),
      data: [
        { name: p('filter_active'), count: stats.actifs, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { name: p('filter_inactive'), count: stats.inactifs, color: 'bg-gradient-to-r from-red-500 to-red-600' },
      ],
    },
    by_currency: {
      title: p('stat_currencies'),
      data: CURRENCY_OPTIONS.map((opt) => ({
        name: opt.label,
        count: priceLists.filter((r) => r.currency === opt.value).length,
        color: 'bg-gradient-to-r from-amber-500 to-amber-600',
      })).filter((item) => item.count > 0),
    },
  };

  const distributionInfo = priceListsDistribution[mainViewMode] || priceListsDistribution.all_lists;

  const mainTabs = [
    { id: 'all_lists', label: t('common.general_view'), icon: LayoutGrid },
    { id: 'by_status', label: t('common.by_status'), icon: CheckCircle },
    { id: 'by_currency', label: p('stat_currencies'), icon: DollarSign },
  ];

  const quickFilterButtons = [
    { id: 'all', label: p('filter_all'), icon: Layers },
    { id: 'actif', label: p('filter_active'), icon: CheckCircle },
    { id: 'inactif', label: p('filter_inactive'), icon: X },
  ];

  const qawaemQuickFilterLabel =
    quickFilterButtons.find((btn) => btn.id === quickFilter)?.label ?? p('filter_all');

  const { columnVisibility, handleToggleColumn, showCol, visibleDataColCount } = useQawaemColumnVisibility([
    'list_number', 'name', 'client_segment', 'validity', 'currency', 'lines', 'status',
  ]);

  const qawaemPriceListColumns = useMemo(
    () => [
      { key: 'list_number', label: p('col_list_number'), checked: columnVisibility.list_number !== false, required: true },
      { key: 'name', label: p('col_name'), checked: columnVisibility.name !== false, required: true },
      { key: 'client_segment', label: p('col_segment'), checked: columnVisibility.client_segment !== false },
      { key: 'validity', label: p('col_validity'), checked: columnVisibility.validity !== false },
      { key: 'currency', label: p('col_currency'), checked: columnVisibility.currency !== false },
      { key: 'lines', label: p('col_lines'), checked: columnVisibility.lines !== false },
      { key: 'status', label: p('col_status'), checked: columnVisibility.status !== false },
      { key: 'actions', label: p('col_actions'), checked: true, required: true },
    ],
    [p, columnVisibility],
  );
  const qawaemVisiblePriceListColumnCount = qawaemPriceListColumns.filter((c) => c.checked).length;
  const tableColSpan = 1 + visibleDataColCount + 1;

  const listStats = useMemo(
    () => [
      { key: 'total', label: p('stat_total'), value: stats.total, icon: Tags, variant: 'copper' },
      { key: 'active', label: p('stat_active'), value: stats.actifs, icon: CheckCircle, variant: 'forest' },
      { key: 'inactive', label: p('stat_inactive'), value: stats.inactifs, icon: X, variant: 'copper' },
      { key: 'lines', label: p('stat_lines'), value: stats.totalLines, icon: Package, variant: 'forest' },
    ],
    [stats, p],
  );

  const advancedOptionsContent = (
    <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 space-y-6">
      <div>
        <h3 className="text-white/80 text-sm font-semibold mb-3 uppercase tracking-wider">
          {t('common.general_view')}
        </h3>
        <div className="flex flex-wrap gap-2">
          {mainTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setMainViewMode(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                mainViewMode === tab.id
                  ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30'
                  : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-white/80 text-sm font-semibold mb-3 uppercase tracking-wider">
          {p('filter_all')}
        </h3>
        <div className="flex flex-wrap gap-2">
          {quickFilterButtons.map((btn) => (
            <button
              key={btn.id}
              type="button"
              onClick={() => setQuickFilter(btn.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                quickFilter === btn.id
                  ? 'bg-purple-500/30 text-purple-200 border-2 border-purple-400/50'
                  : 'bg-white/5 text-white/60 hover:bg-white/10'
              }`}
            >
              <btn.icon className="h-4 w-4" />
              {btn.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-white/80 text-sm font-semibold mb-3 uppercase tracking-wider">
          {distributionInfo.title}
        </h3>
        <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>
          {distributionInfo.data.map((item, index) => {
            const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
            const percentage = total > 0 ? Math.round((item.count / total) * 100) : 0;
            return (
              <div key={index} className="bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="flex justify-between items-center mb-2">
                  <span className="block w-full text-white/70 text-sm font-medium text-right">{item.name}</span>
                  <span className="text-white font-semibold">
                    {item.count} ({percentage}%)
                  </span>
                </div>
                <div className="h-2.5 bg-white/5 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${percentage}%` }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className={`h-full ${item.color} rounded-full`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const baseFilteredLists = useMemo(
    () =>
      priceLists.filter((record) => {
        if (dateRange.start || dateRange.end) {
          const validFrom = record.valid_from ? new Date(record.valid_from) : null;
          if (validFrom) {
            if (dateRange.start) {
              const startOfDay = new Date(dateRange.start);
              startOfDay.setHours(0, 0, 0, 0);
              if (validFrom < startOfDay) return false;
            }
            if (dateRange.end) {
              const endOfDay = new Date(dateRange.end);
              endOfDay.setHours(23, 59, 59, 999);
              if (validFrom > endOfDay) return false;
            }
          }
        }

        const matchesFilter = matchesArticleStatusFilter(record.status, quickFilter);
        const matchesSearch =
          !searchTerm ||
          record.list_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.client_segment?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          resolveClientSegmentLabel(record.client_segment, clientSegmentOptions).toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.currency?.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesFilter && matchesSearch;
      }),
    [priceLists, dateRange, quickFilter, searchTerm, clientSegmentOptions],
  );

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('list_number', (r) => r.list_number || ''),
      textColumnRule('name', (r) => r.name || ''),
      exactColumnRule('client_segment', (r) => r.client_segment || ''),
      textColumnRule('currency', (r) => normalizeBrandCurrency(r.currency) || r.currency || ''),
      textColumnRule('validity', (r) => `${formatDate(r.valid_from)} ${formatDate(r.valid_to)}`),
      textColumnRule('lines', (r) => String(Array.isArray(r.lines) ? r.lines.length : 0)),
      exactColumnRule('status', (r) => r.status || ''),
    ],
    [t],
  );

  const filteredLists = useMemo(
    () => applyColumnFilters(baseFilteredLists, colFilters, columnFilterRules),
    [baseFilteredLists, colFilters, columnFilterRules],
  );
  const displayedLists = useMemo(
    () => sliceArticlesListItems(filteredLists, articlesConfig.itemsPerPage),
    [filteredLists, articlesConfig.itemsPerPage],
  );

  const {
    selectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
    clearSelection,
  } = useFilteredListRowSelection(filteredLists);

  const exportHeaders = useMemo(
    () => [
      { key: 'list_number', label: p('col_list_number') },
      { key: 'name', label: p('col_name') },
      { key: 'currency', label: p('col_currency') },
      { key: 'valid_from', label: p('field_valid_from') },
      { key: 'valid_to', label: p('field_valid_to') },
      { key: 'client_segment', label: p('col_segment') },
      { key: 'status', label: p('col_status') },
      { key: 'price_includes_vat', label: p('field_price_includes_vat') },
      { key: 'notes', label: p('field_notes') },
      { key: 'product_id', label: p('field_product') },
      { key: 'product_name', label: p('field_product') },
      { key: 'variant_id', label: p('field_variant') },
      { key: 'variant_name', label: p('field_variant') },
      { key: 'unit_price', label: p('field_unit_price') },
      { key: 'min_qty', label: p('field_min_qty') },
      { key: 'discount_pct', label: p('field_discount_pct') },
      { key: 'vat_rate', label: p('field_vat_rate') },
    ],
    [p],
  );

  const importSchema = useMemo(() => ({ requiredFields: ['name'], validators: {} }), []);

  const getExportLists = useCallback(
    () => (
      selectedRecords.length > 0
        ? priceLists.filter((list) => selectedRecords.includes(list.id))
        : filteredLists
    ),
    [priceLists, selectedRecords, filteredLists],
  );

  const postImportedList = useCallback(async (item) => {
    const payload = normalizeImportedListPayload(item, vatRate);
    if (!payload) return false;
    await localApiAdapter.post(API_BASE, payload);
    return true;
  }, [vatRate]);

  const importFlatRows = useCallback(async (rows) => {
    const lists = groupFlatImportRows(rows, vatRate);
    let count = 0;
    for (const list of lists) {
      if (await postImportedList(list)) count += 1;
    }
    return count;
  }, [postImportedList, vatRate]);

  const handleExportCSV = useCallback(() => {
    const stamp = new Date().toISOString().split('T')[0];
    exportToCSV(flattenPriceListsForExport(getExportLists()), exportHeaders, `price_lists_${stamp}.csv`);
    setShowExportDropdown(false);
  }, [exportHeaders, getExportLists]);

  const handleExportExcel = useCallback(() => {
    const stamp = new Date().toISOString().split('T')[0];
    exportToExcel(flattenPriceListsForExport(getExportLists()), exportHeaders, `price_lists_${stamp}.xlsx`, p('title'));
    setShowExportDropdown(false);
  }, [exportHeaders, getExportLists, p]);

  const handleExportJSON = useCallback(() => {
    const stamp = new Date().toISOString().split('T')[0];
    const blob = new Blob([JSON.stringify(getExportLists(), null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `price_lists_${stamp}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  }, [getExportLists]);

  const handleImportCSV = useCallback(() => {
    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, exportHeaders, importSchema);
        const rows = result?.data || [];
        if (!rows.length) {
          notifyError(showNotify, p('import_invalid_format'));
          return;
        }
        const count = await importFlatRows(rows);
        await loadPriceLists();
        setShowImportDropdown(false);
        if (count > 0) {
          showNotify(p('import_success', { count }), 'success');
        }
      } catch (error) {
        console.error('Import error:', error);
        notifyError(showNotify, p('import_error'));
      }
    }, '.csv');
  }, [exportHeaders, importSchema, importFlatRows, p, showNotify]);

  const handleImportExcel = useCallback(() => {
    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, exportHeaders, importSchema);
        const rows = result?.data || [];
        if (!rows.length) {
          notifyError(showNotify, p('import_invalid_format'));
          return;
        }
        const count = await importFlatRows(rows);
        await loadPriceLists();
        setShowImportDropdown(false);
        if (count > 0) {
          showNotify(p('import_success', { count }), 'success');
        }
      } catch (error) {
        console.error('Import error:', error);
        notifyError(showNotify, p('import_error'));
      }
    }, '.xlsx,.xls');
  }, [exportHeaders, importSchema, importFlatRows, p, showNotify]);

  const handleImportJSON = useCallback(() => {
    triggerFileInput(async (file) => {
      try {
        const content = await file.text();
        const parsed = JSON.parse(content);
        const rows = Array.isArray(parsed) ? parsed : [parsed];
        let count = 0;
        for (const item of rows) {
          if (await postImportedList(item)) count += 1;
        }
        await loadPriceLists();
        setShowImportDropdown(false);
        if (count > 0) {
          showNotify(p('import_success', { count }), 'success');
        }
      } catch (error) {
        console.error('Import error:', error);
        notifyError(showNotify, p('import_json_error'));
      }
    }, '.json');
  }, [postImportedList, p, showNotify]);

  const importExportProps = useMemo(
    () => buildArticlesImportExportProps({
      showImportDropdown,
      setShowImportDropdown,
      showExportDropdown,
      setShowExportDropdown,
      importDropdownRef,
      exportDropdownRef,
      p,
      t,
      handlers: {
        onImportCSV: handleImportCSV,
        onImportExcel: handleImportExcel,
        onImportJSON: handleImportJSON,
        onExportCSV: handleExportCSV,
        onExportExcel: handleExportExcel,
        onExportJSON: handleExportJSON,
        showJsonImportExport: true,
      },
    }),
    [
      showImportDropdown,
      showExportDropdown,
      t,
      handleImportCSV,
      handleImportExcel,
      handleImportJSON,
      handleExportCSV,
      handleExportExcel,
      handleExportJSON,
    ],
  );

  return (
    <>
      <ArticlesConfigNotice
        message={!articlesConfig.enablePriceLists ? p('notice_price_lists_disabled', { defaultValue: 'قوائم الأسعار معطّلة — فعّلها من إعدادات الأصناف ← التكلفة والأسعار.' }) : null}
      />
      <FacturationListPageLayout
        className="space-y-6"
        containerClassName="employees-container"
        title={p('title')}
        subtitle={p('subtitle')}
        icon={Tags}
        stats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={buildBulkDeleteFilterAction({
          count: selectedRecords.length,
          onClick: handleBulkDelete,
          label: p('delete_selected', { count: selectedRecords.length }),
          title: p('delete_selected', { count: selectedRecords.length }),
        })}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder')}
        onCreate={() => {
          if (!articlesConfig.enablePriceLists) {
            alert(p('price_lists_disabled', { defaultValue: 'قوائم الأسعار معطّلة من إعدادات الأصناف.' }));
            return;
          }
          setSelectedRecord(null);
          setFormData({
            list_number: articlesConfig.autoGeneratePriceListReference !== false ? generateListNumber() : '',
            name: '',
            currency: getBrandDefaultCurrency(),
            valid_from: '',
            valid_to: '',
            client_segment: '',
            status: 'actif',
            notes: '',
            price_includes_vat: articlesConfig.defaultPriceIncludesVat ? 'inclusive' : 'exclusive',
          });
          setLines([]);
          setShowModal(true);
        }}
        createLabel={p('new_item')}
        importExport={importExportProps}
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={t('common.advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
        controlsStrip={
          !qawaem ? (
            <div className="facturation-list-controls-strip p-4 border-b border-white/10">
              <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
                {shouldShowCardsToolbarSearch(viewMode) ? (
                  <div className="relative min-w-0 flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                    <input
                      type="text"
                      placeholder={p('search_placeholder')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ) : null}
                <FacturationViewModeToggle
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  isLight={isDashboardLightMode}
                  cardsTitle={t('common.view_cards')}
                  tableTitle={t('common.view_table')}
                />
              </div>
            </div>
          ) : null
        }
      >
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                  />
                  {showCol('list_number') ? (
                    <TableColumnHeaderFilter
                      columnKey="list_number"
                      label={p('col_list_number')}
                      value={colFilters.list_number ?? ''}
                      onChange={(v) => setColFilter('list_number', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                    />
                  ) : null}
                  {showCol('name') ? (
                    <TableColumnHeaderFilter
                      columnKey="name"
                      label={p('col_name')}
                      value={colFilters.name ?? ''}
                      onChange={(v) => setColFilter('name', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                    />
                  ) : null}
                  {showCol('client_segment') ? (
                    <TableColumnHeaderFilter
                      columnKey="client_segment"
                      label={p('col_segment')}
                      kind="select"
                      options={clientSegmentFilterOptions}
                      value={colFilters.client_segment ?? ''}
                      onChange={(v) => setColFilter('client_segment', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                    />
                  ) : null}
                  {showCol('validity') ? (
                    <TableColumnHeaderFilter
                      columnKey="validity"
                      label={p('col_validity')}
                      value={colFilters.validity ?? ''}
                      onChange={(v) => setColFilter('validity', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                    />
                  ) : null}
                  {showCol('currency') ? (
                    <TableColumnHeaderFilter
                      columnKey="currency"
                      label={p('col_currency')}
                      value={colFilters.currency ?? ''}
                      onChange={(v) => setColFilter('currency', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                    />
                  ) : null}
                  {showCol('lines') ? (
                    <TableColumnHeaderFilter
                      columnKey="lines"
                      label={p('col_lines')}
                      value={colFilters.lines ?? ''}
                      onChange={(v) => setColFilter('lines', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      align="center"
                      thClassName="text-center"
                    />
                  ) : null}
                  {showCol('status') ? (
                    <TableColumnHeaderFilter
                      columnKey="status"
                      label={p('col_status')}
                      kind="select"
                      options={statusColumnFilterOptions}
                      value={colFilters.status ?? ''}
                      onChange={(v) => setColFilter('status', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      align="center"
                      thClassName="text-center"
                    />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemPriceListColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisiblePriceListColumnCount}
                    totalCount={qawaemPriceListColumns.length}
                    thClassName={QAWAEM_TABLE_ACTIONS_TH_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {displayedLists.length > 0 ? (
                  displayedLists.map((record, index) => (
                    <tr
                      key={record.id}
                      className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}
                    >
                      <QawaemTableSelectRowCell
                        checked={selectedRecords.includes(record.id)}
                        onChange={() => handleSelectRecord(record.id)}
                      />
                      {showCol('list_number') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold font-mono">{record.list_number || '—'}</span>
                          </div>
                        </td>
                      ) : null}
                      {showCol('name') ? (
                        <td className="px-6 py-4 text-white font-semibold">{record.name}</td>
                      ) : null}
                      {showCol('client_segment') ? (
                        <td className="px-6 py-4 text-white/80">{formatClientSegment(record.client_segment)}</td>
                      ) : null}
                      {showCol('validity') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col text-sm">
                            <span className="text-white/80">{formatDate(record.valid_from)}</span>
                            <span className="text-white/50 text-xs">→ {formatDate(record.valid_to)}</span>
                          </div>
                        </td>
                      ) : null}
                      {showCol('currency') ? (
                        <td className="px-6 py-4 text-white font-mono">{fmtCurrency(record.currency)}</td>
                      ) : null}
                      {showCol('lines') ? (
                        <td className="px-6 py-4 text-center">
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30">
                            {Array.isArray(record.lines) ? record.lines.length : 0}
                          </span>
                        </td>
                      ) : null}
                      {showCol('status') ? (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge
                              status={record.status}
                              recordId={record.id}
                              options={STATUS_OPTIONS}
                              onChange={handleStatusChange}
                              title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })}
                            />
                          </div>
                        </td>
                      ) : null}
                      <QawaemTableStickyActionsTd>
                        <QawaemErpRowActions
                          onView={() => handleView(record)}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </QawaemTableStickyActionsTd>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={tableColSpan} className="text-center py-12 text-white/60">
                      <Tags size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_title')}</p>
                      <p className="text-sm text-white/40 mt-2">{p('empty_hint')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 rounded-b-3xl">
            {displayedLists.length > 0 ? (
              displayedLists.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center flex-wrap gap-3 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Tags size={20} className="text-blue-400" />
                          {record.name}
                        </h3>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/10 text-white/80 font-mono">
                          {record.list_number || '—'}
                        </span>
                        <QawaemClickableStatusBadge
                          status={record.status}
                          recordId={record.id}
                          options={STATUS_OPTIONS}
                          onChange={handleStatusChange}
                          title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })}
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-white/70">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_header')}</h5>
                          <div className="space-y-1">
                            <div>
                              <span className="font-medium">{p('field_list_number')}:</span> {record.list_number || '—'}
                            </div>
                            <div>
                              <span className="font-medium">{p('col_name')}:</span> {record.name}
                            </div>
                            <div>
                              <span className="font-medium">{p('field_currency')}:</span> {fmtCurrency(record.currency)}
                            </div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_validity')}</h5>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <Calendar size={14} className="text-emerald-400" />
                              {formatDate(record.valid_from)} → {formatDate(record.valid_to)}
                            </div>
                            <div>
                              <span className="font-medium">{p('field_client_segment')}:</span>{' '}
                              {formatClientSegment(record.client_segment)}
                            </div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_lines')}</h5>
                          <div className="space-y-1">
                            <div>
                              <span className="font-medium">{p('col_lines')}:</span>{' '}
                              {p('lines_count', { count: Array.isArray(record.lines) ? record.lines.length : 0 })}
                            </div>
                            {Array.isArray(record.lines) && record.lines.slice(0, 3).map((line, i) => (
                              <div key={i} className="text-xs text-white/60 truncate">
                                • {line.product_name || line.product_id || p('empty_dash')}
                                {line.variant_name ? ` / ${line.variant_name}` : ''} — {formatPrice(line.unit_price)}
                                {line.vat_rate != null ? ` (${line.vat_rate}% ${p('col_vat')})` : ''}
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_options')}</h5>
                          <p className="text-white/60 text-xs">{record.notes || '—'}</p>
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => handleView(record)}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Tags size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_title')}</p>
              </div>
            )}
          </div>
        )}
      </FacturationListPageLayout>

      {showModal &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            onClick={resetForm}
            role="presentation"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden"
              style={{
                boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)',
                width: '90%',
                maxWidth: '1200px',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Tags className="h-6 w-6 text-blue-400" />
                  {selectedRecord ? p('modal_edit') : p('modal_new')}
                </h2>
                <div className="flex items-center gap-3">
                  <button
                    type="submit"
                    form="price-list-form"
                    className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
                  >
                    <CheckCircle size={18} />
                    {selectedRecord ? t('common.save') : t('common.create')}
                  </button>
                  <button onClick={resetForm} className="text-white/70 hover:text-white transition-colors">
                    <X size={24} />
                  </button>
                </div>
              </div>

              <div className="overflow-y-auto p-6 flex-1 min-h-0">
                <form id="price-list-form" onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_list_number')}</label>
                      <input
                        type="text"
                        value={formData.list_number}
                        onChange={(e) => setFormData({ ...formData, list_number: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                        placeholder={p('placeholder_list_number')}
                        dir="ltr"
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">
                        <RequiredLabelText text={p('col_name')} required />
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={p('placeholder_name')}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_currency')}</label>
                      <CustomSelect
                        value={formData.currency || ''}
                        onChange={(val) => setFormData({ ...formData, currency: val || '' })}
                        options={CURRENCY_OPTIONS}
                        placeholder={p('placeholder_currency')}
                        searchable={false}
                        creatable={false}
                        disabled={currencyLocked}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_valid_from')}</label>
                      <CustomDatePicker
                        value={formData.valid_from}
                        onChange={(v) => setFormData({ ...formData, valid_from: v })}
                        placeholder={p('placeholder_date')}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_valid_to')}</label>
                      <CustomDatePicker
                        value={formData.valid_to}
                        onChange={(v) => setFormData({ ...formData, valid_to: v })}
                        placeholder={p('placeholder_date')}
                        minDate={formData.valid_from || undefined}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_client_segment')}</label>
                      <CustomSelect
                        value={formData.client_segment || ''}
                        onChange={(val) => setFormData({ ...formData, client_segment: val || '' })}
                        options={clientSegmentOptions}
                        placeholder={p('placeholder_client_segment')}
                        searchable
                        creatable
                        autoSelect={false}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">
                        <RequiredLabelText text={p('col_status')} required />
                      </label>
                      <CustomSelect
                        value={formData.status}
                        onChange={(val) => setFormData({ ...formData, status: val })}
                        options={formStatusOptions}
                        placeholder={p('placeholder_status')}
                        searchable={false}
                        creatable={false}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_price_includes_vat')}</label>
                      <CustomSelect
                        value={formData.price_includes_vat}
                        onChange={(val) => setFormData({ ...formData, price_includes_vat: val || 'exclusive' })}
                        options={priceIncludesVatOptions}
                        searchable={false}
                        creatable={false}
                      />
                    </div>
                    <div className="min-w-0 md:col-span-3">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_notes')}</label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        rows={3}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={p('placeholder_notes')}
                      />
                    </div>
                  </div>

                  <div className="space-y-4 pt-2 border-t border-white/10">
                    <p className="text-xs text-white/50">{pricingTierHint}</p>
                    <p className="text-xs text-white/50">{p('vat_default_hint', { rate: vatRate })}</p>
                    {lines.length > 0 && (
                      <div className="bg-white/5 rounded-lg border border-white/10 overflow-x-auto">
                        <table className="w-full min-w-[900px]">
                          <thead className="glass-table-head">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-white/60">{p('field_product')}</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-white/60">{p('field_variant')}</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-white/60">
                                {p('field_unit_price')} ({currencySuffix})
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-white/60">{p('field_min_qty')}</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-white/60">{p('field_discount_pct')}</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-white/60">{p('field_vat_rate')}</th>
                              <th className="px-3 py-2 text-center text-xs font-medium text-white/60">{p('col_actions')}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-white/5">
                            {lines.map((line, idx) => (
                              <tr key={idx} className="hover:bg-white/5">
                                <td className="px-2 py-2">
                                  <CustomSelect
                                    value={line.product_id}
                                    onChange={(productId) => handleLineProductSelect(idx, productId)}
                                    options={productOptions}
                                    placeholder={p('placeholder_product')}
                                    searchable
                                  />
                                  {line.product_name && (
                                    <p className="text-xs text-white/50 mt-1 truncate">{line.product_name}</p>
                                  )}
                                </td>
                                <td className="px-2 py-2">
                                  <CustomSelect
                                    value={line.variant_id}
                                    onChange={(variantId) => handleLineVariantSelect(idx, variantId)}
                                    options={getVariantOptions(line.product_id)}
                                    placeholder={p('placeholder_variant')}
                                    searchable
                                    disabled={!line.product_id}
                                  />
                                  {line.variant_name && (
                                    <p className="text-xs text-white/50 mt-1 truncate">{line.variant_name}</p>
                                  )}
                                </td>
                                <td className="px-2 py-2">
                                  <CustomNumberInput
                                    step={0.01}
                                    value={line.unit_price}
                                    onChange={(v) => updateLine(idx, { unit_price: v })}
                                    placeholder={p('placeholder_unit_price')}
                                  />
                                  {getPriceTierLabel(line.unit_price) ? (
                                    <p className="text-xs text-amber-300/80 mt-1">{getPriceTierLabel(line.unit_price)}</p>
                                  ) : null}
                                </td>
                                <td className="px-2 py-2">
                                  <CustomNumberInput
                                    step={0.01}
                                    value={line.min_qty}
                                    onChange={(v) => updateLine(idx, { min_qty: v })}
                                    placeholder={p('placeholder_min_qty')}
                                  />
                                </td>
                                <td className="px-2 py-2">
                                  <CustomNumberInput
                                    step={0.01}
                                    value={line.discount_pct}
                                    onChange={(v) => updateLine(idx, { discount_pct: v })}
                                    placeholder={p('placeholder_discount_pct')}
                                  />
                                </td>
                                <td className="px-2 py-2">
                                  <CustomNumberInput
                                    step={0.01}
                                    value={line.vat_rate}
                                    onChange={(v) => updateLine(idx, { vat_rate: v })}
                                    placeholder={String(vatRate)}
                                  />
                                </td>
                                <td className="px-2 py-2 text-center">
                                  <button
                                    type="button"
                                    onClick={() => removeLine(idx)}
                                    className="p-1.5 text-red-400 hover:text-red-300 transition-colors"
                                    title={p('remove_line')}
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={addLine}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                        qawaem || isDashboardLightMode
                          ? 'border border-dashed border-[#6e9b6e]/50 bg-[#eef6ee] text-[#2e452e] hover:bg-[#6e9b6e]/12 hover:border-[#6e9b6e]/70'
                          : 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-100 border border-blue-500/30'
                      }`}
                    >
                      <Plus size={16} />
                      {p('add_line')}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>,
          document.body
        )}

      {showViewModal &&
        selectedRecord &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            onClick={() => setShowViewModal(false)}
            role="presentation"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
              style={{
                boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)',
                width: '95%',
                maxWidth: '1100px',
              }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Tags className="h-6 w-6 text-blue-400" />
                  {selectedRecord.name}
                </h2>
                <button onClick={() => setShowViewModal(false)} className="text-white/60 hover:text-white transition-colors">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <Hash size={18} className="text-emerald-400" />
                    <h3 className="text-lg font-semibold text-white">{p('section_header')}</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-white/60 text-xs uppercase mb-1">{p('field_list_number')}</p>
                      <p className="text-white font-semibold font-mono">{selectedRecord.list_number || '—'}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-xs uppercase mb-1">{p('col_name')}</p>
                      <p className="text-white font-semibold">{selectedRecord.name}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-xs uppercase mb-1">{p('field_currency')}</p>
                      <p className="text-white font-semibold font-mono">{fmtCurrency(selectedRecord.currency)}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-xs uppercase mb-1">{p('field_price_includes_vat')}</p>
                      <p className="text-white font-semibold">
                        {selectedRecord.price_includes_vat === 'inclusive'
                          ? p('price_vat_inclusive')
                          : p('price_vat_exclusive')}
                      </p>
                    </div>
                    <div>
                      <p className="text-white/60 text-xs uppercase mb-1">{p('col_status')}</p>
                      {getStatusBadge(selectedRecord.status)}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                      <Calendar size={18} className="text-blue-400" />
                      <h3 className="text-lg font-semibold text-white">{p('section_validity')}</h3>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <span className="text-white/60 text-sm">{p('field_valid_from')}:</span>{' '}
                        <span className="text-white font-semibold">{formatDate(selectedRecord.valid_from)}</span>
                      </div>
                      <div>
                        <span className="text-white/60 text-sm">{p('field_valid_to')}:</span>{' '}
                        <span className="text-white font-semibold">{formatDate(selectedRecord.valid_to)}</span>
                      </div>
                      <div>
                        <span className="text-white/60 text-sm">{p('field_client_segment')}:</span>{' '}
                        <span className="text-white font-semibold">{formatClientSegment(selectedRecord.client_segment)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                      <FileJson size={18} className="text-amber-400" />
                      <h3 className="text-lg font-semibold text-white">{p('section_options')}</h3>
                    </div>
                    <p className="text-white/70 text-sm italic">{selectedRecord.notes || '—'}</p>
                  </div>
                </div>

                {Array.isArray(selectedRecord.lines) && selectedRecord.lines.length > 0 && (
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                      <Package size={18} className="text-purple-400" />
                      <h3 className="text-lg font-semibold text-white">
                        {p('section_lines')} ({selectedRecord.lines.length})
                      </h3>
                    </div>
                    <div className="glass-table-scroll overflow-x-auto">
                      <table className="w-full">
                        <thead className="glass-table-head">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-white/60">{p('field_product')}</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-white/60">{p('field_variant')}</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-white/60">
                              {p('field_unit_price')} ({currencySuffix})
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-white/60">{p('field_min_qty')}</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-white/60">{p('field_discount_pct')}</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-white/60">{p('field_vat_rate')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                          {selectedRecord.lines.map((line, idx) => (
                            <tr key={idx}>
                              <td className="px-3 py-2 text-white/90">{line.product_name || line.product_id || p('empty_dash')}</td>
                              <td className="px-3 py-2 text-white/80">{line.variant_name || p('no_variant')}</td>
                              <td className="px-3 py-2 text-right text-white font-mono">
                                {formatPrice(line.unit_price)}
                                {getPriceTierLabel(line.unit_price) ? (
                                  <span className="block text-xs text-amber-300/80">{getPriceTierLabel(line.unit_price)}</span>
                                ) : null}
                              </td>
                              <td className="px-3 py-2 text-right text-white font-mono">{line.min_qty ?? 0}</td>
                              <td className="px-3 py-2 text-right text-amber-300 font-mono">
                                {line.discount_pct ?? 0}%
                              </td>
                              <td className="px-3 py-2 text-right text-emerald-300 font-mono">
                                {line.vat_rate ?? vatRate}%
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={() => setShowViewModal(false)}
                className="w-full mt-6 bg-white/5 hover:bg-white/10 text-white py-3 rounded-xl font-semibold transition-all border border-white/10"
              >
                {t('common.close')}
              </button>
            </motion.div>
          </div>,
          document.body
        )}
            <AppConfirmDialog
        open={deleteConfirm.open}
        title={t('common.delete', 'حذف')}
        message={
          deleteConfirm.ids.length > 1
            ? p('bulk_delete_confirm', { count: deleteConfirm.ids.length })
            : p('delete_confirm')
        }
        confirmLabel={t('common.delete', 'حذف')}
        cancelLabel={t('common.cancel', 'إلغاء')}
        danger
        onCancel={closeDeleteConfirm}
        onConfirm={executeDeleteConfirmed}
      />
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default PriceListsPage;
